import {
    Container,
    NineSlicePlane,
    Sprite,
    Texture,
    Rectangle,
    Circle,
    Polygon,
    Graphics,
} from 'pixi.js';
import { Focusable } from './FocusManager';
import { LoadContext, Tween } from '../utils';
import { GlowFilter } from '@pixi/filter-glow';
import { StyledBitmapText } from './StyledBitmapText';
import { BitmapTextStyleOpts } from './BitmapTextStyle';

export interface TextSettings
{
    content: string;
    style: BitmapTextStyleOpts;
    offset?: {x: number, y: number}
}

// width for focus frame
const FRAME_WIDTH = 6;

export const FOCUS_FILTERS = [new GlowFilter({
    color: 0xffffff,
    distance: 20,
    outerStrength: 4,
    quality: 0.05
})];

export class Button extends Container implements Focusable
{
    public sprite: Sprite|NineSlicePlane = null;
    protected text: StyledBitmapText = null;//Text = null;
    protected focusBox: NineSlicePlane = null;
    private _highlight: boolean = false;

    public static preload()
    {
        LoadContext.current.add('SharedUI', './assets/shared/SharedUI{size}.json');
    }

    constructor(texture?:Texture, text?:TextSettings)
    {
        super();
        if (texture)
        {
            this.sprite = new Sprite(texture);
            this.sprite.anchor.set(0.5);
            this.addChild(this.sprite);
        }
        if (text && text.style)
        {
            this.text = new StyledBitmapText(text.content, text.style);
            this.text.updateText();
            this.text.anchor.set(0.5, 0.6);
            if (text.offset)
            {
                this.text.position.set(text.offset.x, text.offset.y);
            }
            this.addChild(this.text);
        }
        this.enabled = true;
        // after we are set up, make the focus -
        // allows subclasses to have their own size info or just override sprite
        setTimeout(this.createFocus.bind(this), 0);
    }

    protected createFocus()
    {
        // TODO: focus box should probably become its own class, like UIBoxBG in for the game scene
        this.focusBox = new NineSlicePlane(LoadContext.current.getSpritesheet('SharedUI').textures['highlight_frame'], FRAME_WIDTH, FRAME_WIDTH, FRAME_WIDTH, FRAME_WIDTH);
        const {width, height} = this.getSize();
        // make box just a tad bigger than whatever the size is
        this.focusBox.width = width + 2;
        this.focusBox.height = height + 2;
        this.focusBox.position.set(-this.focusBox.width / 2, -this.focusBox.height / 2);
        this.addChild(this.focusBox);
        this.focusBox.visible = false;
    }

    protected getSize()
    {
        const target = this.sprite || this.text;
        return {width: target.width, height: target.height};
    }

    public createHitRect(buffer = 0)
    {
        const {width, height} = this.getSize();
        this.hitArea = new Rectangle(-width / 2 - buffer, -height / 2 - buffer, width + buffer * 2, height + buffer * 2);
    }

    public createHitCircle(buffer = 0)
    {
        const {width} = this.getSize();
        this.hitArea = new Circle(0, 0, width / 2 + buffer);
    }

    public createHitTriangleN()
    {
        this.hitArea = new Polygon(
            [
                -100,-95,
                0,65,
                100, -95
            ]
        );
    }

    public createHitTriangleW()
    {
        this.hitArea = new Polygon(
            [
                -80,-90,
                65,0,
                -80, 90
            ]
        );
    }

    public createHitTriangleS()
    {
        this.hitArea = new Polygon(
            [
                -100,100,
                0,-65,
                100, 100
            ]
        );
    }
    
    public createHitTriangleE()
    {
        this.hitArea = new Polygon(
            [
                80,-95,
                -70,0,
                80, 85
            ]
        );
    }

    public get highlight() { return this._highlight }
    public set highlight(highlight: boolean)
    {
        this.filters = highlight ? FOCUS_FILTERS : null;
        if (highlight && !this._highlight)
        {
            Tween.get(this.scale, {loop: true}).to({x: 1.1, y: 1.1}, 750, 'quadInOut').to({x: 1, y: 1}, 750, 'quadInOut');
            this._highlight = true;
        }
        else if (!highlight)
        {
            Tween.removeTweens(this.scale);
            this.scale.set(1);
            this._highlight = false;
        }
    }

    public get skip() { return !this.worldVisible; }

    public get focus() { return this.focusBox.visible; }
    public set focus(focused) {
        this.focusBox.visible = focused;
        if (focused)
        {
            this.emit('pointerover');
            this.emit('focused');
        }
        else
        {
            this.emit('pointerout');
            this.emit('unfocused');
        }
    }

    public get focusVisible() { return this.focusBox.visible; }
    public set focusVisible(visible) {
        this.focusBox.visible = visible;
    }

    public activate()
    {
        if (this.enabled)
        {
            this.emit('pointertap');
        }
    }

    public get label() { return this.text?.text; }
    public set label(label)
    {
        if (this.text)
        {
            this.text.text = label;
        }
    }

    public get enabled() { return this.interactive; }
    public set enabled(enabled)
    {
        this.interactive = enabled;
        this.cursor = enabled ? 'pointer' : null;
    }

    public destroy()
    {
        // remove any highlight tweens going on
        Tween.removeTweens(this.scale);
        this.highlight = false;
        super.destroy({children: true});
    }
}